import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Popover,
  Box,
  Chip,
  Divider,
} from "@mui/material";
import { Binding, BindingType, RecurringType } from "@lib/types/binding";
import { useTranslation } from "react-i18next";
import InformationView from "@components/InformationView";
import { getDaysLabel, parseValue } from "@lib/utils/string";

interface BindingCardProps {
  binding: Binding;
}

const BindingCard: React.FC<BindingCardProps> = ({ binding }) => {
  const { t } = useTranslation();
  const { id, binding_type, status } = binding;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const binding_info = [
    {
      label: t("user.bindings.bindingType"),
      value: t(`user.bindings.${binding_type}`),
      columnSize: 8,
    },
    {
      label: t("user.bindings.driver"),
      value: binding.driver_details?.name,
      columnSize: 4,
    },
    {
      label: t("user.bindings.unit"),
      value: parseValue(binding.unit_details?.name),
      columnSize: 4,
    },
    ...(binding_type !== "recurring"
      ? [
          {
            label: t("user.bindings.startDatetime"),
            value: parseValue(binding.start_datetime),
            columnSize: 4,
          },
          {
            label: t("user.bindings.endDatetime"),
            value: parseValue(binding.end_datetime),
            columnSize: 4,
          },
        ]
      : []),
  ];

  const schedules = binding?.schedules.map((field) => ({
    recurringTypeLabel:
      field.recurring_type === RecurringType.MONTHLY
        ? t("user.bindings.monthly")
        : t("user.bindings.weekly"),
    recurringValue:
      field.recurring_type === RecurringType.MONTHLY
        ? parseValue(field.day_of_month)
        : getDaysLabel(field.days_of_week || []),
    startDate: {
      label: t("user.bindings.startDate"),
      value: field.start_time,
    },
    endDate: {
      label: t("user.bindings.endDate"),
      value: field.end_time,
    },
  }));

  const handleLearnMore = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? `binding-popover-${id}` : undefined;

  return (
    <Card sx={{ width: "100%", margin: 1 }}>
      <CardContent>
        <InformationView data={binding_info} columns={8} />
      </CardContent>
      {binding.binding_type === BindingType.RECURRING && (
        <>
          <CardActions>
            <Button
              size="small"
              onClick={handleLearnMore}
              aria-describedby={popoverId}
            >
              {t("user.bindings.moreDetails")}
            </Button>
          </CardActions>
          <Popover
            id={popoverId}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="h6">
                {t("user.bindings.bindingDetails")}
              </Typography>
              {binding.start_datetime && (
                <Typography variant="body2">
                  {t("user.bindings.startDate")}: {binding.start_datetime}
                </Typography>
              )}
              {binding.end_datetime && (
                <Typography variant="body2">
                  {t("user.bindings.endDate")}: {binding.end_datetime}
                </Typography>
              )}

              {binding.binding_type === BindingType.RECURRING && (
                <>
                  <Typography variant="body1">
                    {t("user.bindings.schedules")}
                  </Typography>
                  {schedules.map((schedule, index) => (
                    <>
                      <Typography variant="body2">
                        {schedule.recurringTypeLabel}: {schedule.recurringValue}
                      </Typography>
                      <Typography variant="body2">
                        {schedule.startDate.label}: {schedule.startDate.value}
                      </Typography>
                      <Typography variant="body2">
                        {schedule.endDate.label}: {schedule.endDate.value}
                      </Typography>
                      {index < schedules.length - 1 && <Divider />}
                    </>
                  ))}
                </>
              )}
            </Box>
          </Popover>
        </>
      )}
    </Card>
  );
};

export default BindingCard;
